import { Box, TextField, Typography } from "@mui/material";
import { useMutation } from "react-query";
import { AuthAPI } from "../../../../../utils/api/requests/auth-requests";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { ChangePasswordForm } from "../../../../auth/types";
import { joiResolver } from "@hookform/resolvers/joi";
import { getChangePasswordSchema } from "../../../../../utils/schemas/AuthSchemas";
import { AxiosError } from "../../../../../utils/api/axiosErrorType";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";

export const ChangePassword = () => {
  const { t } = useTranslation();

  const form = useForm<ChangePasswordForm>({
    mode: "onChange",
    resolver: joiResolver(getChangePasswordSchema()),
  });

  const { handleSubmit, control, formState, reset } = form;

  const { mutate, isLoading } = useMutation({
    mutationFn: AuthAPI.changePassword,

    onSuccess: () => {
      toast.success(t("changePassword.successfullyChanged"));
      reset();
    },
    onError: (error) => {
      const err = error as AxiosError;
      toast.error(err?.response?.data?.detail);
    },
  });

  const onSubmit = async (formData: ChangePasswordForm) => {
    mutate({
      current_password: formData.current_password,
      new_password: formData.new_password,
    });
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "100%", md: "50%" },
        boxShadow:
          "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
        borderRadius: "16px",
        padding: "12px",
        mb: "24px",
        color: "rgb(108, 115, 127)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{ fontSize: "1.25rem", color: "rgb(17, 25, 39)", mb: "12px" }}
      >
        {t("changePassword.changePassword")}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        <Controller
          control={control}
          name="current_password"
          defaultValue=""
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                inputRef={ref}
                type="password"
                label={t("changePassword.currentPassword")}
                variant="outlined"
                size="small"
                autoComplete="off"
                sx={{
                  marginBottom: "12px",
                }}
                error={!!error}
              />
            </>
          )}
        />
        <Controller
          control={control}
          name="new_password"
          defaultValue=""
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                inputRef={ref}
                type="password"
                label={t("changePassword.newPassword")}
                variant="outlined"
                autoComplete="off"
                size="small"
                sx={{
                  marginBottom: "12px",
                }}
                error={!!error}
              />
            </>
          )}
        />
        <Controller
          control={control}
          name="repeatPassword"
          defaultValue=""
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                inputRef={ref}
                type="password"
                label={t("changePassword.confirmPassword")}
                variant="outlined"
                autoComplete="off"
                size="small"
                sx={{
                  marginBottom: "12px",
                }}
                error={!!error}
              />
            </>
          )}
        />
        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <LoadingButton
            disabled={!formState.isValid}
            type="submit"
            variant="contained"
            size="medium"
            color={"primary"}
            loading={isLoading}
          >
            {t("submit")}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
